import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Mail, Phone, MapPin, Clock, MessageCircle } from 'lucide-react'
import { settingsService } from '../services/api'
import Logo from './Logo'

const Footer = () => {
  const { data: settings } = useQuery({
    queryKey: ['storeSettings'],
    queryFn: settingsService.getSettings,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })

  const currentYear = new Date().getFullYear()

  return (
    <footer className="bg-gray-900 text-white" style={{ marginTop: 'auto' }}>
   
     
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <div className="lg:col-span-2">
            <div className="mb-6">
              <Logo variant="header" showText={true} textClassName="text-white" />
            </div>
            <p className="text-gray-300 mb-6 max-w-md">
              {settings?.store_description || 'نخبة المسابيح - نقدم لكم أجود أنواع المسابيح والسبح بأفضل الأسعار. جودة عالية وخدمة متميزة.'}
            </p>
          </div>

          {/* Contact Information */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">تواصل معنا</h3>
            <div className="space-y-4">
              {settings?.phone_number && (
                <div className="flex items-center space-x-3 space-x-reverse">
                  <Phone className="h-5 w-5 text-primary-400 flex-shrink-0" />
                  <a 
                    href={`tel:${settings.phone_number}`}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {settings.phone_number}
                  </a>
                </div>
              )}
              
              {settings?.whatsapp_number && (
                <div className="flex items-center space-x-3 space-x-reverse">
                  <MessageCircle className="h-5 w-5 text-green-400 flex-shrink-0" />
                  <a 
                    href={`https://wa.me/${settings.whatsapp_number.replace(/[^0-9]/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    WhatsApp
                  </a>
                </div>
              )}

              {settings?.email && (
                <div className="flex items-center space-x-3 space-x-reverse">
                  <Mail className="h-5 w-5 text-primary-400 flex-shrink-0" />
                  <a 
                    href={`mailto:${settings.email}`}
                    className="text-gray-300 hover:text-white transition-colors"
                  >
                    {settings.email}
                  </a>
                </div>
              )}

              <div className="flex items-start space-x-3 space-x-reverse">
                <MapPin className="h-5 w-5 text-primary-400 flex-shrink-0 mt-0.5" />
                <span className="text-gray-300">
                  {settings?.address || 'Kuwait'}
                </span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-6 text-white">روابط سريعة</h3>
            <div className="space-y-3">
              <Link 
                to="/" 
                className="block text-gray-300 hover:text-white transition-colors"
              >
                الرئيسية
              </Link>
              <Link 
                to="/products" 
                className="block text-gray-300 hover:text-white transition-colors"
              >
                المنتجات
              </Link>
              <Link 
                to="/categories" 
                className="block text-gray-300 hover:text-white transition-colors"
              >
                الفئات
              </Link>
              <Link 
                to="/return-policy" 
                className="block text-gray-300 hover:text-white transition-colors"
              >
                📋 Return Policy - سياسة الإرجاع
              </Link>
            </div>
            
            {/* Additional Sections */}
            <div className="mt-8 space-y-4">
              <div>
                <h4 className="text-lg font-semibold text-white mb-2">سياسة الخصوصية</h4>
                <p className="text-gray-300 text-sm">
                  نحن نحترم خصوصيتكم ونحمي بياناتكم الشخصية
                </p>
              </div>
              
              
            </div>

            {/* Social Media Links */}
            <div className="mt-6">
              <h4 className="text-lg font-semibold mb-4 text-white">تابعونا</h4>
              <div className="flex space-x-4 space-x-reverse">
                <a 
                  href="https://www.instagram.com/seb7atq8" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-pink-400 transition-colors"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                </a>
                <a 
                  href="https://www.tiktok.com/@seb7atkw" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-gray-300 hover:text-red-400 transition-colors"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm text-center md:text-left">
              © {currentYear} {settings?.store_name || 'سبحات'}. جميع الحقوق محفوظة.
            </p>
          </div>
        </div>
      </div>
    </footer>
  )
}

export default Footer